import { FC, useState } from "react";
import { Notification } from "@/models/notification/Notifications";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import {
  groupNotificationsByDocumentAndDay,
  NotificationGroup,
} from "@/utils/notifications/notificationGrouping";
import NotificationItem from "./NotificationItem";
import { cn } from "@/lib/utils";

export type NotificationGroupListProps = {
  notifications: Notification[];
  dismissNotification: (notificationId: string) => void;
  dismissNotifications: (notificationIds: string[]) => void;
  isDismissingNotification: Record<string, boolean> | undefined;
  onNavigate?: () => void;
  /** Optional class for the container. */
  className?: string;
};

const NotificationGroupList: FC<NotificationGroupListProps> = ({
  notifications,
  dismissNotification,
  dismissNotifications,
  isDismissingNotification,
  onNavigate,
  className,
}) => {
  const [openGroupKeys, setOpenGroupKeys] = useState<Set<string>>(new Set());
  const groups = groupNotificationsByDocumentAndDay(notifications);

  const setGroupOpen = (groupKey: string, open: boolean) => {
    setOpenGroupKeys((prev) => {
      const next = new Set(prev);
      if (open) next.add(groupKey);
      else next.delete(groupKey);
      return next;
    });
  };

  if (groups.length === 0) {
    return null;
  }

  const renderGroup = (group: NotificationGroup) => {
    const ids = group.notifications.map((n) => n.id);
    const isDismissingGroup = ids.some(
      (id) => isDismissingNotification?.[id]
    );

    const content = (
      <div className="flex flex-col">
        {group.notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            dismissNotification={dismissNotification}
            isDismissingNotification={isDismissingNotification}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    );

    const useCollapsible = group.notifications.length > 1;

    if (useCollapsible) {
      const isOpen = openGroupKeys.has(group.groupKey);
      return (
        <Collapsible
          key={group.groupKey}
          open={isOpen}
          onOpenChange={(open) => setGroupOpen(group.groupKey, open)}
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className={cn(
                "w-full flex items-center justify-between gap-2 py-2 px-2 rounded-md",
                "hover:bg-accent text-left text-sm font-medium",
                "border-b border-border/50"
              )}
            >
              <span className="flex items-center gap-1.5 min-w-0">
                {isOpen ? (
                  <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                )}
                <span className="truncate" title={group.label}>
                  {group.label}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  ({group.notifications.length})
                </span>
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="size-6 shrink-0 opacity-70 hover:opacity-100"
                disabled={isDismissingGroup}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  dismissNotifications(ids);
                }}
                title="Afvis gruppe"
              >
                {isDismissingGroup ? (
                  <Spinner className="size-3.5" />
                ) : (
                  <X className="size-3.5" />
                )}
              </Button>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>{content}</CollapsibleContent>
        </Collapsible>
      );
    }

    return <div key={group.groupKey}>{content}</div>;
  };

  return (
    <div className={cn("flex flex-col", className)}>
      {groups.map((group) => renderGroup(group))}
    </div>
  );
};

export default NotificationGroupList;